import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../UI/Modal';
import creditService from '../../api/services/credit.service';

interface CreditDetailModalProps {
  isOpen: boolean;
  creditId: string | null;
  onClose: () => void;
}

/* =========================================================
   Source meta — one label + colour per source
========================================================= */

const SOURCE_META: Record<
  string,
  { label: string; className: string }
> = {
  'verify.et': {
    label: 'Verify.et',
    className: 'bg-secondary-100 text-secondary-700',
  },
  system: {
    label: 'Admin Credit',
    className: 'bg-primary-100 text-primary-700',
  },
  referral: {
    label: 'Referral',
    className: 'bg-purple-100 text-purple-700',
  },
  signup: {
    label: 'Signup Bonus',
    className: 'bg-emerald-100 text-emerald-700',
  },
  game: {
    label: 'Game Win',
    className: 'bg-amber-100 text-amber-700',
  },
  transfer: {
    label: 'Transfer',
    className: 'bg-sky-100 text-sky-700',
  },
};

function sourceMeta(source: string) {
  return (
    SOURCE_META[source] || {
      label: source,
      className: 'bg-gray-100 text-gray-700',
    }
  );
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-start justify-between gap-4 py-1.5 border-b border-gray-50 last:border-0">
    <span className="text-xs text-gray-500 flex-shrink-0">{label}</span>
    <span className="text-xs font-medium text-gray-900 text-right break-all">
      {value}
    </span>
  </div>
);

const statusStyle: Record<string, string> = {
  completed: 'bg-green-50 text-green-700',
  pending: 'bg-yellow-50 text-yellow-700',
  failed: 'bg-red-50 text-red-700',
};

const CreditDetailModal: React.FC<CreditDetailModalProps> = ({
  isOpen,
  creditId,
  onClose,
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['credit-detail', creditId],
    queryFn: () => creditService.getOne(creditId as string),
    enabled: isOpen && !!creditId,
    staleTime: 60 * 1000,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Credit Details" size="md">
      {isLoading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-solid border-primary-500 border-t-transparent" />
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 py-4">
          {error instanceof Error ? error.message : 'Failed to load credit'}
        </p>
      )}

      {data && (
        <div className="space-y-4">
          {/* Amount header */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] text-primary-700 uppercase tracking-wide">
                Amount
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {data.currency} {data.amount.toLocaleString()}
              </p>
            </div>
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase ${
                statusStyle[data.status] || 'bg-gray-100 text-gray-700'
              }`}
            >
              {data.status}
            </span>
          </div>

          {/* User info */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              User
            </h4>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <Row label="Name" value={data.userName} />
              <Row label="Username" value={`@${data.userUsername || '—'}`} />
              <Row label="Telegram ID" value={data.telegramId} />
              <Row label="Phone" value={data.userPhone || '—'} />
              <Row
                label="Balance After"
                value={`${data.currency} ${data.balanceAfter.toLocaleString()}`}
              />
            </div>
          </div>

          {/* Credit info */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Credit Info
            </h4>
            <div className="bg-gray-50 rounded-lg px-3 py-2">
              <Row
                label="Type"
                value={<span className="capitalize">{data.type.replace('_', ' ')}</span>}
              />
              <Row
                label="Source"
                value={
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${sourceMeta(data.source).className}`}
                  >
                    {sourceMeta(data.source).label}
                  </span>
                }
              />
              {data.referenceNumber && (
                <Row label="Reference" value={data.referenceNumber} />
              )}
              {data.bank && <Row label="Bank" value={data.bank} />}
              {data.accountSuffix && (
                <Row label="Account Suffix" value={data.accountSuffix} />
              )}
              {data.senderName && <Row label="Sender" value={data.senderName} />}
              {data.receiverName && (
                <Row label="Receiver" value={data.receiverName} />
              )}
              {data.receiverAccount && (
                <Row label="Receiver Account" value={data.receiverAccount} />
              )}
              {data.requestId && <Row label="Request ID" value={data.requestId} />}
              {data.note && <Row label="Note" value={data.note} />}
              <Row
                label="Created"
                value={new Date(data.createdAt).toLocaleString()}
              />
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CreditDetailModal;